import React from "react";
import { Icon, notify } from "../registry";
import type { SiteBundle, DomainRow } from "./types";
import { Toggle, OFFLINE_MSG, copyText } from "./fields";
import { fmtDateTime, primaryHost } from "./util";

/* ============================================================
   Website builder — growth (migration #32; server/src/sites/{stats,gsc,
   weeklyreport,entri}.js).
   • GrowthPanel: at the top of the editor's section list — the stats card
     (visitors, page views, calls, quote requests; 7 / 30 days; a sparkline;
     top pages; where visitors came from; Google numbers when Search Console
     is connected; the weekly-report toggle) and the day-1 checklist.
   • SearchConsoleCard: the Domain tab's Search Console block (not connected /
     verifying / connected / error), with "Give me access in Search Console".
   • EntriConnect: "Connect automatically" on a domain that's waiting for DNS.
   Without a service (sandbox) everything shows sample numbers and says so.
   ============================================================ */

type Totals = { views: number; visitors: number; calls: number; texts: number; quoteClicks: number; quoteRequests: number; topPages: { path: string; views: number }[]; sources: Record<string, number> };
type Day = { day: string; views: number; visitors: number; calls: number; texts: number; quoteClicks: number; quoteRequests: number };
export type GscRow = { host: string; state: "verifying" | "connected" | "error" | "removed"; error?: string | null; verifiedAt?: string | null; connectedAt?: string | null; lastPullAt?: string | null; nextTryAt?: string | null; owners?: string[] };
type CheckItem = { id: string; label: string; tab: string; fix: string; done: boolean; progress?: string };
export type Growth = {
  setup: boolean; message?: string; configured: { gsc: boolean; reports: boolean; entri: boolean };
  stats: null | { days: number; from: string; to: string; firstDay: string | null; series: Day[]; d7: Totals; d30: Totals };
  search: null | { hosts: string[]; clicks: number; impressions: number; ctr: number; position: number | null; queries: { key: string; clicks: number; impressions: number }[]; pages: { key: string; clicks: number }[]; pulledAt?: string | null; periodEnd?: string | null };
  gsc: GscRow[];
  checklist: { items: CheckItem[]; done: number; total: number; canDismiss: boolean; dismissed: boolean };
  report: { enabled: boolean };
  sample?: boolean;
  /* tracked calls (calltracking.js, #40): null before #40 */
  calls?: null | { tracking: boolean; number: string | null; d7: CallSum; d30: CallSum };
};
type CallSum = { total: number; answered: number; missed: number };

/* the stats card's Calls tile: real calls from the tracked number when it's on, else taps on the number */
export function callsTile(g: Pick<Growth, "calls">, t: { calls: number; texts: number }, range: "d7" | "d30") {
  const c = g.calls;
  if (c && c.tracking) {
    const s = c[range];
    return { value: s.total, note: s.total ? `${s.answered} answered · ${s.missed} missed` : "from your tracked number", title: "Calls to your website's tracked number" };
  }
  return { value: t.calls, note: t.texts ? `+ ${t.texts} text${t.texts === 1 ? "" : "s"}` : "tapped to call", title: "Taps on your phone number" };
}

/* ---------------- pure helpers (web/scripts/unit-sitegrowth.mjs) ---------------- */

/* A sparkline's SVG paths for values across a w×h box. */
function sparkPath(values: number[], w: number, h: number, pad = 2) {
  const n = values.length;
  const max = Math.max(1, ...values.map((v) => Number(v) || 0));
  if (!n) return { line: "", area: "", max };
  const x = (i: number) => (n === 1 ? w / 2 : pad + (i * (w - 2 * pad)) / (n - 1));
  const y = (v: number) => h - pad - ((Number(v) || 0) / max) * (h - 2 * pad);
  const pts = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`);
  const line = "M" + pts.join(" L");
  const area = `${line} L${x(n - 1).toFixed(1)},${h} L${x(0).toFixed(1)},${h} Z`;
  return { line, area, max };
}

/* 1,234 · 12.3k · 1.2M */
function fmtCount(n: number) {
  const v = Math.round(Number(n) || 0);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1).replace(/\.0$/, "") + "M";
  if (v >= 10_000) return (v / 1000).toFixed(v >= 100_000 ? 0 : 1).replace(/\.0$/, "") + "k";
  return v.toLocaleString("en-US");
}

/* where visitors came from → rows in a fixed order, with whole percentages */
const SOURCE_LABELS: [string, string][] = [["google", "Google"], ["direct", "Direct / typed in"], ["social", "Social"], ["other", "Other sites"]];
function sourceRows(sources: Record<string, number> | null | undefined) {
  const s = sources || {};
  const total = SOURCE_LABELS.reduce((t, [k]) => t + (Number(s[k]) || 0), 0);
  return SOURCE_LABELS.map(([key, label]) => ({ key, label, n: Number(s[key]) || 0, pct: total ? Math.round(((Number(s[key]) || 0) / total) * 100) : 0 }));
}

/* one word for a site's Search Console state */
function gscStateOf(rows: GscRow[] | null | undefined, configured: boolean) {
  const live = (rows || []).filter((r) => r && r.state !== "removed");
  if (live.some((r) => r.state === "connected")) return "connected";
  if (live.some((r) => r.state === "verifying")) return "verifying";
  if (live.some((r) => r.state === "error")) return "error";
  return configured ? "none" : "off";
}

/* believable sample numbers for the sandbox (no service) — deterministic */
function sampleGrowth(host: string, now: number): Growth {
  const DAY = 86400000;
  const today = Date.parse(new Date(now).toISOString().slice(0, 10) + "T00:00:00Z");
  const series: Day[] = [];
  for (let i = 29; i >= 0; i--) {
    const t = 29 - i;
    const visitors = Math.round(6 + t * 0.45 + ((t * 7) % 5) + (new Date(today - i * DAY).getUTCDay() === 1 ? 5 : 0));
    series.push({ day: new Date(today - i * DAY).toISOString().slice(0, 10), visitors, views: Math.round(visitors * 2.4), calls: t % 4 === 0 ? 1 : 0, texts: t % 9 === 0 ? 1 : 0, quoteClicks: t % 3 === 0 ? 1 : 0, quoteRequests: t % 6 === 0 ? 1 : 0 });
  }
  const sum = (d: Day[], k: keyof Day) => d.reduce((s, x) => s + (Number(x[k]) || 0), 0);
  const tot = (d: Day[], scale: number): Totals => ({
    views: sum(d, "views"), visitors: sum(d, "visitors"), calls: sum(d, "calls"), texts: sum(d, "texts"), quoteClicks: sum(d, "quoteClicks"), quoteRequests: sum(d, "quoteRequests"),
    topPages: [{ path: "/", views: Math.round(96 * scale) }, { path: "/styles/vinyl", views: Math.round(41 * scale) }, { path: "/cost", views: Math.round(27 * scale) }, { path: "/locations/de-pere", views: Math.round(15 * scale) }],
    sources: { google: Math.round(58 * scale), direct: Math.round(31 * scale), social: Math.round(14 * scale), other: Math.round(6 * scale) },
  });
  return {
    setup: true, sample: true, configured: { gsc: true, reports: true, entri: true },
    stats: { days: 30, from: series[0].day, to: series[29].day, firstDay: series[0].day, series, d7: tot(series.slice(-7), 0.3), d30: tot(series, 1) },
    search: { hosts: [host], clicks: 64, impressions: 2310, ctr: 64 / 2310, position: 12.6, queries: [{ key: "fence company near me", clicks: 21, impressions: 480 }, { key: "vinyl fence cost", clicks: 12, impressions: 610 }, { key: "fence repair green bay", clicks: 9, impressions: 140 }], pages: [], pulledAt: new Date(now - 5 * 3600000).toISOString() },
    gsc: [{ host, state: "connected", connectedAt: new Date(now - 9 * DAY).toISOString(), lastPullAt: new Date(now - 5 * 3600000).toISOString(), owners: [] }],
    checklist: { items: [
      { id: "publish", label: "Publish your site", tab: "", fix: "Publish", done: true },
      { id: "domain", label: "Connect your domain", tab: "domain", fix: "Connect", done: false },
      { id: "photos", label: "Add 3 photos from your jobs", tab: "home", fix: "Add photos", done: false, progress: "1/3" },
      { id: "quote", label: "Turn on the quote widget", tab: "business", fix: "Turn on", done: true },
      { id: "review", label: "Add your Google review link", tab: "business", fix: "Add link", done: true },
      { id: "blog", label: "Publish your first blog post", tab: "blog", fix: "Write a post", done: false },
      { id: "gsc", label: "Connect Google Search Console", tab: "domain", fix: "Connect", done: true },
    ], done: 4, total: 7, canDismiss: false, dismissed: false },
    report: { enabled: true },
    calls: { tracking: true, number: "(920) 555-9000", d7: { total: 4, answered: 3, missed: 1 }, d30: { total: 11, answered: 9, missed: 2 } },
  };
}

/* the live site, opened by its owner: the site app remembers that browser and never counts it */
export function selfUrl(url: string) { try { const u = new URL(url); u.searchParams.set("__fwself", "1"); return u.toString(); } catch (e) { return url; } }

/* ---------------- data ---------------- */
export function useGrowth({ live, sample, bundle }: { live: boolean; sample: boolean; bundle: SiteBundle }) {
  const DB: any = (window as any).VFP_DB;
  const host = primaryHost(bundle);
  const [data, setData] = React.useState<Growth | null>(() => (sample || !live ? sampleGrowth(host, Date.now()) : null));
  const [err, setErr] = React.useState("");
  const load = React.useCallback(async () => {
    if (sample || !live) return;
    try { setErr(""); setData(await DB.siteGrowth()); }
    catch (e: any) { setErr(e.message || "Couldn't load your website numbers"); }
  }, [live, sample]);
  React.useEffect(() => { load(); }, [load]);
  return { data, setData, err, reload: load };
}

/* ---------------- the stats card ---------------- */
function Spark({ values, label }: { values: number[]; label: string }) {
  const W = 300, H = 46;
  const p = sparkPath(values, W, H);
  return (
    <svg className="wg-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={label}>
      <path d={p.area} className="wg-spark-area" />
      <path d={p.line} className="wg-spark-line" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function StatsCard({ g, bundle, live, sample, onGo, onReport }: { g: Growth; bundle: SiteBundle; live: boolean; sample: boolean; onGo: (tab: string) => void; onReport: (on: boolean) => void }) {
  const [range, setRange] = React.useState<"d7" | "d30">("d30");
  const site = bundle.site!;
  const host = primaryHost(bundle);
  const published = site.status === "live" && !!site.published;
  const s = g.stats;
  const t = s ? s[range] : null;
  const series = s ? s.series.slice(range === "d7" ? -7 : -30) : [];
  const nothing = !!t && !t.views && !t.calls && !t.quoteRequests && !(g.calls && g.calls.tracking && g.calls[range].total);
  const gscState = gscStateOf(g.gsc, g.configured.gsc);
  const q = g.search;
  const liveUrl = "https://" + host;

  return (
    <div className="card wg-card">
      <div className="wg-head">
        <div style={{ minWidth: 0 }}>
          <span className="kicker kicker-teal">Your website</span>
          <h2 className="display wg-title">Visitors &amp; leads{sample && <span className="chip amber" style={{ marginLeft: 8, fontSize: 10.5, padding: "1px 7px", verticalAlign: 3 }}>Sample</span>}</h2>
        </div>
        {s && <div className="ws-seg" role="tablist" aria-label="Date range">
          <button type="button" className={range === "d7" ? "on" : ""} onClick={() => setRange("d7")}>7 days</button>
          <button type="button" className={range === "d30" ? "on" : ""} onClick={() => setRange("d30")}>30 days</button>
        </div>}
      </div>

      {!g.setup ? (
        <p className="ws-note wg-setup"><Icon name="lock" size={13} /> {g.message || "Visitor numbers need a database update (#32)."}</p>
      ) : !published ? (
        <div className="wg-empty"><b>No visitors yet.</b> Publish your site to start counting — we count visits without cookies and never count your own previews.</div>
      ) : !t ? null : (
        <>
          <div className="wg-tiles">
            <div className="wg-tile"><span>Visitors</span><b className="tnum">{fmtCount(t.visitors)}</b></div>
            <div className="wg-tile"><span>Page views</span><b className="tnum">{fmtCount(t.views)}</b></div>
            {(() => { const ct = callsTile(g, t, range); return <div className="wg-tile" title={ct.title}><span>Calls</span><b className="tnum">{fmtCount(ct.value)}</b><em>{ct.note}</em></div>; })()}
            <div className="wg-tile"><span>Quote requests</span><b className="tnum" style={{ color: t.quoteRequests ? "var(--teal)" : undefined }}>{fmtCount(t.quoteRequests)}</b><em title={`${t.quoteClicks} taps on “Get a quote”`}>{t.quoteClicks ? `${t.quoteClicks} quote tap${t.quoteClicks === 1 ? "" : "s"}` : "form + widget"}</em></div>
          </div>
          {nothing ? (
            <div className="wg-empty">
              <b>No visitors yet: publish and share your link.</b> Put <code>{host}</code> on your Google Business Profile, your Facebook page and your email signature.
              <div className="row gap-8" style={{ marginTop: 8 }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => copyText(liveUrl, "Link copied")}><Icon name="clipboard" size={13} />Copy link</button>
              </div>
            </div>
          ) : (
            <>
              <div className="wg-spark-wrap">
                <Spark values={series.map((d) => d.visitors)} label={`Visitors per day, last ${series.length} days`} />
                <div className="wg-spark-axis faint"><span>{fmtShort(series[0] && series[0].day)}</span><span>visitors per day</span><span>{fmtShort(series[series.length - 1] && series[series.length - 1].day)}</span></div>
              </div>
              <div className="wg-cols">
                <div>
                  <div className="wg-sub">Top pages</div>
                  {t.topPages.length ? t.topPages.slice(0, 4).map((p) => (
                    <div key={p.path} className="wg-row"><code title={p.path}>{p.path === "/" ? "Home" : p.path}</code><span className="tnum">{fmtCount(p.views)}</span></div>
                  )) : <div className="faint wg-row">—</div>}
                </div>
                <div>
                  <div className="wg-sub">Where visitors came from</div>
                  {sourceRows(t.sources).map((r) => (
                    <div key={r.key} className="wg-bar" title={`${r.n} visit${r.n === 1 ? "" : "s"}`}>
                      <span>{r.label}</span><span className="tnum faint">{r.pct}%</span>
                      <i style={{ width: r.pct + "%" }} />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}

      <div className="wg-google">
        <span className="wg-g"><Icon name="search" size={13} />Google</span>
        {gscState === "connected" && q ? (
          <span className="wg-google-txt"><b className="tnum">{fmtCount(q.clicks)}</b> clicks · <b className="tnum">{fmtCount(q.impressions)}</b> times shown in search{q.position ? <> · avg. position <b className="tnum">{q.position}</b></> : null} <span className="faint">(28 days)</span>
            {q.queries && q.queries[0] ? <span className="faint wg-q"> · top search “{q.queries[0].key}”</span> : null}</span>
        ) : gscState === "connected" ? <span className="wg-google-txt faint">Connected — Google's first numbers arrive within a day or two.</span>
          : gscState === "verifying" ? <span className="wg-google-txt faint">Search Console is verifying your site with Google…</span>
          : gscState === "error" ? <button type="button" className="rv-link" onClick={() => onGo("domain")}>Search Console needs attention →</button>
          : gscState === "none" ? <button type="button" className="rv-link" onClick={() => onGo("domain")}>Connect Search Console to see Google searches →</button>
          : <span className="wg-google-txt faint">Search Console isn't switched on for this server yet.</span>}
      </div>

      <div className="wg-report">
        <Toggle label="Email me a weekly report" value={g.report.enabled} disabled={!g.setup}
          hint={<>Monday mornings: visitors, calls, quote requests and one thing to do next.{!g.configured.reports && !sample ? " Not switched on for this server yet — your choice is saved." : ""}</>}
          onChange={(on: boolean) => { if (!live && !sample) { notify(OFFLINE_MSG); return; } onReport(on); }} />
      </div>
    </div>
  );
}
const fmtShort = (d?: string) => { const x = d ? Date.parse(d + "T12:00:00Z") : NaN; return Number.isFinite(x) ? new Date(x).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }) : ""; };

/* ---------------- the day-1 checklist ---------------- */
function Checklist({ list, onFix, onDismiss }: { list: Growth["checklist"]; onFix: (item: CheckItem) => void; onDismiss: () => void }) {
  const pct = Math.round((list.done / Math.max(1, list.total)) * 100);
  return (
    <div className="card wg-card wg-check">
      <div className="wg-head">
        <div style={{ minWidth: 0 }}>
          <span className="kicker">Day-1 checklist</span>
          <h2 className="display wg-title">{list.done === list.total ? "All set" : "Get your site working for you"}</h2>
        </div>
        <span className="faint tnum" style={{ fontSize: 12.5, whiteSpace: "nowrap" }}>{list.done} of {list.total} done</span>
      </div>
      <div className="wg-progress" role="progressbar" aria-valuemin={0} aria-valuemax={list.total} aria-valuenow={list.done}><i style={{ width: pct + "%" }} /></div>
      <div className="wg-items">
        {list.items.map((it) => (
          <div key={it.id} className={"wg-item" + (it.done ? " done" : "")}>
            <span className="wg-dot">{it.done ? <Icon name="check" size={12} /> : null}</span>
            <span className="wg-item-txt">{it.label}{it.progress && !it.done ? <span className="faint tnum"> · {it.progress}</span> : null}</span>
            {!it.done && <button type="button" className="btn btn-ghost btn-sm wg-fix" onClick={() => onFix(it)}>{it.fix}<Icon name="chevright" size={13} /></button>}
          </div>
        ))}
      </div>
      {list.canDismiss && <div className="row" style={{ justifyContent: "flex-end", marginTop: 8 }}><button type="button" className="rv-link" onClick={onDismiss}>Hide the checklist</button></div>}
    </div>
  );
}

export function GrowthPanel({ bundle, live, sample, onGo, onPublish }: { bundle: SiteBundle; live: boolean; sample: boolean; onGo: (tab: string) => void; onPublish: () => void }) {
  const DB: any = (window as any).VFP_DB;
  const { data, setData, err } = useGrowth({ live, sample, bundle });
  if (err && !data) return <div className="card pad ws-note" style={{ marginBottom: 12 }}>{err}</div>;
  if (!data) return <div className="card pad wg-card muted" style={{ marginBottom: 12 }}><span className="ws-spin" style={{ marginRight: 8 }} />Loading your website numbers…</div>;
  const setReport = async (on: boolean) => {
    const prev = data.report.enabled;
    setData({ ...data, report: { enabled: on } });
    if (sample || !live) { notify(on ? "Weekly report on (sample)." : "Weekly report off (sample)."); return; }
    try { const r = await DB.siteReport(on); setData((d) => (d ? { ...d, report: r.report } : d)); notify(on ? "You'll get a weekly report on Monday mornings." : "Weekly report off."); }
    catch (e: any) { setData((d) => (d ? { ...d, report: { enabled: prev } } : d)); notify(e.message || "Couldn't save that", { kind: "error" }); }
  };
  const dismiss = async () => {
    if (sample || !live) { setData({ ...data, checklist: { ...data.checklist, dismissed: true } }); return; }
    try { await DB.siteChecklistDismiss(); setData((d) => (d ? { ...d, checklist: { ...d.checklist, dismissed: true } } : d)); }
    catch (e: any) { notify(e.message || "Couldn't hide it", { kind: "error" }); }
  };
  const fix = (it: CheckItem) => { if (it.id === "publish") onPublish(); else onGo(it.tab || ""); };
  return (
    <div className="wg-panel">
      <StatsCard g={data} bundle={bundle} live={live} sample={sample} onGo={onGo} onReport={setReport} />
      {!data.checklist.dismissed && <Checklist list={data.checklist} onFix={fix} onDismiss={dismiss} />}
    </div>
  );
}

/* ---------------- Search Console (the Domain tab) ---------------- */
export function SearchConsoleCard({ bundle, live, sample }: { bundle: SiteBundle; live: boolean; sample: boolean }) {
  const DB: any = (window as any).VFP_DB;
  const { data, setData, err } = useGrowth({ live, sample, bundle });
  const [busy, setBusy] = React.useState("");
  const [email, setEmail] = React.useState("");
  const site = bundle.site!;
  const published = site.status === "live" && !!site.published;
  const configured = sample || !!(data ? data.configured.gsc : (bundle as any).gsc);
  const rows = (data && data.gsc || []).filter((r) => r.state !== "removed");
  const state = gscStateOf(rows, configured);
  const main = rows.find((r) => r.state === "connected") || rows.find((r) => r.state === "verifying") || rows.find((r) => r.state === "error") || null;
  const q = data && data.search;
  const owners = Array.from(new Set(rows.flatMap((r) => r.owners || [])));

  const connect = async () => {
    if (sample || !live) { notify("That's the sample site — on your real site this verifies it with Google for you."); return; }
    setBusy("connect");
    try { const r = await DB.siteGscConnect(); setData((d) => (d ? { ...d, gsc: r.gsc } : d)); notify("Verifying with Google — it usually takes under an hour. You can leave this page."); }
    catch (e: any) { notify(e.message || "Couldn't start Search Console", { kind: "error" }); }
    setBusy("");
  };
  const addOwner = async () => {
    const e = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { notify("Enter the email address you sign in to Google with.", { kind: "error" }); return; }
    if (sample || !live) { notify("That's the sample site — on your real site we add you as an owner in Search Console."); return; }
    setBusy("owner");
    try { const r = await DB.siteGscOwner(e); setData((d) => (d ? { ...d, gsc: r.gsc } : d)); setEmail(""); notify(`${e} can now open your site in Search Console.`); }
    catch (x: any) { notify(x.message || "Google wouldn't add that address", { kind: "error" }); }
    setBusy("");
  };

  const chip = state === "connected" ? <span className="chip green">Connected</span>
    : state === "verifying" ? <span className="chip amber"><span className="ws-pulse" />Verifying</span>
    : state === "error" ? <span className="chip ws-chip-red">Problem</span>
    : state === "none" ? <span className="chip">Not connected</span> : <span className="chip">Off</span>;

  return (
    <div className="card pad wg-gsc">
      <div className="row between" style={{ gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <span className="kicker kicker-teal">Google Search Console</span>
          <h3 className="display" style={{ fontSize: 18, margin: "6px 0 4px" }}>See what people search to find you</h3>
        </div>
        {chip}
      </div>
      {err && !data && <p className="ws-note" style={{ color: "var(--red)" }}>{err}</p>}
      {data && !data.setup && <p className="ws-note"><Icon name="lock" size={12} /> {data.message}</p>}

      {state === "off" && <p className="muted" style={{ fontSize: 13.5, margin: "4px 0 0" }}>Search Console isn't switched on for this server yet. Fence Works support turns it on — nothing for you to set up.</p>}

      {state === "none" && (
        <>
          <p className="muted" style={{ fontSize: 13.5, margin: "4px 0 12px", maxWidth: 620 }}>We verify <b>{primaryHost(bundle)}</b> with Google for you, send Google your sitemap, and show your clicks and top searches here. You don't need a Google login for it.</p>
          <button type="button" className="btn btn-primary btn-sm" disabled={!published || busy === "connect" || (data ? !data.setup : false)} onClick={connect}><Icon name="search" size={14} />{busy === "connect" ? "Starting…" : "Connect Search Console"}</button>
          {!published && <p className="faint" style={{ fontSize: 12, margin: "8px 0 0" }}>Publish your site first — Google checks the live page.</p>}
        </>
      )}

      {state === "verifying" && main && (
        <p className="muted" style={{ fontSize: 13.5, margin: "4px 0 0" }}>We've added Google's verification tag to <b>{main.host}</b>. Google usually confirms within an hour{main.nextTryAt ? <> — next check {fmtDateTime(main.nextTryAt)}</> : null}. You can leave this page.
          {main.error ? <span className="faint" style={{ display: "block", marginTop: 4, fontSize: 12.5 }}>Last check: {main.error}</span> : null}</p>
      )}

      {state === "error" && main && (
        <>
          <div className="ws-err">{main.error || "Google couldn't verify your site."}</div>
          <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 10 }} disabled={busy === "connect"} onClick={connect}><Icon name="undo" size={13} />{busy === "connect" ? "Starting…" : "Try again"}</button>
        </>
      )}

      {state === "connected" && (
        <>
          <p className="faint" style={{ fontSize: 12.5, margin: "2px 0 10px" }}>{rows.filter((r) => r.state === "connected").map((r) => r.host).join(" · ")}{main && main.lastPullAt ? ` · updated ${fmtDateTime(main.lastPullAt)}` : ""} · sitemap sent</p>
          {q ? (
            <>
              <div className="wg-tiles wg-tiles-4">
                <div className="wg-tile"><span>Clicks</span><b className="tnum">{fmtCount(q.clicks)}</b></div>
                <div className="wg-tile"><span>Shown in search</span><b className="tnum">{fmtCount(q.impressions)}</b></div>
                <div className="wg-tile"><span>Click rate</span><b className="tnum">{q.impressions ? (q.ctr * 100).toFixed(1) + "%" : "—"}</b></div>
                <div className="wg-tile"><span>Avg. position</span><b className="tnum">{q.position != null ? q.position : "—"}</b></div>
              </div>
              <div className="wg-sub" style={{ marginTop: 12 }}>Top searches · last 28 days</div>
              {q.queries.length ? q.queries.slice(0, 5).map((x) => (
                <div key={x.key} className="wg-row"><span>{x.key}</span><span className="tnum faint">{x.clicks} click{x.clicks === 1 ? "" : "s"} · {fmtCount(x.impressions)} shown</span></div>
              )) : <p className="faint" style={{ fontSize: 12.5, margin: 0 }}>No searches yet — a new site takes a few weeks to show up.</p>}
            </>
          ) : <p className="muted" style={{ fontSize: 13.5, margin: 0 }}>Connected. Google's first numbers usually arrive within a day or two.</p>}
          <div className="wg-owner">
            <div className="wg-sub">Give me access in Search Console</div>
            <p className="faint" style={{ fontSize: 12.5, margin: "0 0 8px" }}>Want to open it in Google yourself? Enter your Google account and we'll add you as an owner.</p>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              <input className="input ws-input" style={{ flex: "1 1 220px", maxWidth: 320 }} type="email" value={email} placeholder="you@gmail.com" autoCapitalize="none" spellCheck={false}
                onChange={(e: any) => setEmail(e.target.value)} onKeyDown={(e: any) => e.key === "Enter" && addOwner()} />
              <button type="button" className="btn btn-outline btn-sm" disabled={!email.trim() || busy === "owner"} onClick={addOwner}>{busy === "owner" ? "Adding…" : "Add me"}</button>
            </div>
            {owners.length > 0 && <p className="faint" style={{ fontSize: 12, margin: "8px 0 0" }}>Owners you added: {owners.join(", ")}</p>}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- "Connect automatically" (Entri) ---------------- */
let entriLoading: Promise<any> | null = null;
function loadEntri(src: string): Promise<any> {
  const w: any = window;
  if (w.entri && typeof w.entri.showEntri === "function") return Promise.resolve(w.entri);
  if (!/^https:\/\/cdn\.goentri\.com\//.test(src)) return Promise.reject(new Error("Unexpected connect script."));
  if (!entriLoading) {
    entriLoading = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = src; s.async = true;
      s.onload = () => (w.entri && typeof w.entri.showEntri === "function" ? resolve(w.entri) : reject(new Error("The connect helper didn't load.")));
      s.onerror = () => { entriLoading = null; reject(new Error("Couldn't load the connect helper — check your connection and try again.")); };
      document.head.appendChild(s);
    });
  }
  return entriLoading;
}

export function EntriConnect({ domain, live, sample, onDone }: { domain: DomainRow; live: boolean; sample: boolean; onDone: (ok: boolean) => void }) {
  const DB: any = (window as any).VFP_DB;
  const [busy, setBusy] = React.useState(false);
  const start = async () => {
    if (sample || !live) { notify("That's the sample site — on your real site this opens your registrar's sign-in and adds the records for you."); return; }
    setBusy(true);
    try {
      const r = await DB.siteDomainEntri(domain.id);
      const entri = await loadEntri(r.sdkUrl);
      const onClose = (ev: any) => {
        window.removeEventListener("onEntriClose", onClose);
        const d = (ev && ev.detail) || {};
        if (d.success) { notify("Done — your registrar has the records. It shows Live here as soon as DNS catches up, usually within minutes."); onDone(true); }
        else if (d.domain) notify("Nothing was changed at your registrar. Try again, or add the records yourself below.");
      };
      window.addEventListener("onEntriClose", onClose);
      entri.showEntri(r.config);
    } catch (e: any) { notify(e.message || "Couldn't start automatic connect — add the records below instead.", { kind: "error" }); }
    setBusy(false);
  };
  return (
    <div className="wg-entri">
      <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={start}><Icon name="bolt" size={14} />{busy ? "Opening…" : "Connect automatically"}</button>
      <span className="faint">Sign in to your domain company and we add the records for you. Nothing else changes — never your email.</span>
    </div>
  );
}
